// SPDX-License-Identifier: AGPL-3.0-only

import { requestJson } from "./http.js";

export interface SystemVersion {
  readonly productVersion: string;
  readonly gitCommit: string;
  readonly publicApiUrl: string;
}

export interface SystemSource {
  readonly repositoryUrl: string;
  readonly revision: string;
  readonly license: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function requireString(payload: Record<string, unknown>, fieldName: string): string {
  const value: unknown = payload[fieldName];
  if (typeof value !== "string" || value.trim() === "") {
    throw new Error(`System information response field ${fieldName} is invalid`);
  }
  return value;
}

function readSystemVersion(payload: unknown): SystemVersion {
  if (!isRecord(payload)) {
    throw new Error("System version response is invalid");
  }
  return {
    productVersion: requireString(payload, "productVersion"),
    gitCommit: requireString(payload, "gitCommit"),
    publicApiUrl: requireString(payload, "publicApiUrl"),
  };
}

function readSystemSource(payload: unknown): SystemSource {
  if (!isRecord(payload)) {
    throw new Error("System source response is invalid");
  }
  return {
    repositoryUrl: requireString(payload, "repositoryUrl"),
    revision: requireString(payload, "revision"),
    license: requireString(payload, "license"),
  };
}

/** Formats a full Git object ID compactly for the footer without obscuring non-Git development labels. */
export function formatBuildCommit(gitCommit: string): string {
  return /^[0-9a-f]{7,}$/i.test(gitCommit) ? gitCommit.slice(0, 12) : gitCommit;
}

export async function loadSystemVersion(): Promise<SystemVersion> {
  return readSystemVersion(await requestJson("/api/v1/system/version"));
}

export async function loadSystemSource(): Promise<SystemSource> {
  return readSystemSource(await requestJson("/api/v1/system/source"));
}
